import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender
} from '@tanstack/react-table';
import Select from 'react-select';
import ButtonsGroup from '../ButtonsGroup';
import Breadcrumb from '../Breadcrumb';
import { accountManagers } from '../../utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Custom Search Component
const MySearch = React.memo(({ searchText, onSearchChange, alternTitle }) => {
  const handleSearch = (e) => {
    const value = e.target.value;
    onSearchChange(value);
  };

  return (
    <div
      className="flex items-center px-4 bg-white"
      style={{ height: '59px', borderBottom: '1px solid #e5e7eb' }}>
      <div
        className="flex items-center gap-2 pr-4 border-r border-[#dee2e6]"
        style={{ minWidth: '262px' }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          className="w-4 h-4 text-muted-foreground">
          <path
            fill="rgb(131, 146, 165)"
            d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
          />
        </svg>
        <Input
          type="search"
          className="border-0 shadow-none focus-visible:ring-0"
          placeholder={alternTitle ? `Search ${alternTitle}` : 'Search'}
          value={searchText}
          onChange={handleSearch}
        />
      </div>
    </div>
  );
});

const DataTables = ({
  data,
  columns: propsColumns,
  view,
  viewState,
  buttonsCallback = () => {},
  userRole,
  noDataIndication,
  expandRow,
  rowEvents,
  alternTitle,
  title = null,
  handleModal,
  showTotal,
  rowStyle,
  loading,
  children,
  onAddNewClientClick
}) => {
  const [filter, setFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15
  });

  // Convert columns from react-bootstrap-table format to TanStack format
  const tanstackColumns = useMemo(() => {
    return propsColumns
      .filter((col) => !col.hidden)
      .map((col) => ({
        id: col.dataField,
        accessorKey: col.dataField,
        header: col.text,
        cell: col.formatter
          ? ({ row }) =>
              col.formatter(row.original[col.dataField], row.original)
          : ({ getValue }) => getValue(),
        size: col.headerStyle?.()?.width
          ? parseInt(col.headerStyle().width)
          : undefined,
        meta: {
          headerStyle: col.headerStyle?.() || {},
          cellStyle: col.style?.() || {}
        }
      }));
  }, [propsColumns]);

  // Apply account manager filter
  const filteredByManager = useMemo(() => {
    if (!data) return [];
    if (!filter || filter.value === '0') return data;
    return data.filter((item) => item.accountOwner === filter.value);
  }, [data, filter]);

  // Apply search filter
  const filteredData = useMemo(() => {
    if (!filteredByManager) return [];
    if (!searchText) return filteredByManager;

    const searchLower = searchText.toLowerCase();
    return filteredByManager.filter((row) => {
      return propsColumns.some((column) => {
        const cellValue = column.dataField ? row[column.dataField] : null;
        if (cellValue === null || cellValue === undefined) return false;
        const stringValue = String(cellValue).toLowerCase();
        return stringValue.includes(searchLower);
      });
    });
  }, [filteredByManager, searchText, propsColumns]);

  const table = useReactTable({
    data: filteredData,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination
    },
    onPaginationChange: setPagination
  });

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const finalCount = filteredData.length;

  // Custom styles for react-select
  const customStyles = {
    container: (provided) => ({
      ...provided,
      width: '120px'
    }),
    control: (provided) => ({
      ...provided,
      maxHeight: '32px',
      minHeight: '32px'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3766a8' : 'white',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3766a8' : '#f0f0f0'
      }
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '4px'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px'
    })
  };

  return (
    <>
      <MySearch
        searchText={searchText}
        onSearchChange={setSearchText}
        alternTitle={alternTitle}
      />
      <div
        className="p-6 bg-gray-50"
        style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
        {/* Header section */}
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: '1rem' }}>
          <div className="flex flex-col" style={{ marginBottom: '0' }}>
            <h1
              className="text-xl font-semibold"
              style={{
                marginBottom: '0.25rem',
                fontSize: '1.25rem',
                lineHeight: '1.75rem'
              }}>
              {title}{' '}
              {view === undefined && finalCount >= 0 ? `(${finalCount})` : null}
            </h1>
            <p
              className="text-sm text-muted-foreground"
              style={{
                marginBottom: '0',
                color: '#6b7280',
                fontSize: '0.875rem'
              }}>
              {alternTitle === 'reviews'
                ? 'Manage and respond to customer reviews'
                : 'Manage your content'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {view === undefined && (
              <>
                <div className="flex items-center">
                  <label className="mr-2 text-muted-foreground text-sm">
                    Account Manager:
                  </label>
                  <Select
                    isDisabled={loading}
                    styles={customStyles}
                    value={
                      filter
                        ? {
                            value: filter.value,
                            label:
                              accountManagers.find(
                                (item) => item.id === filter.value
                              )?.full_name || 'All'
                          }
                        : { value: '0', label: 'All' }
                    }
                    onChange={(val) => {
                      handleFilterChange({ value: val.value });
                    }}
                    defaultValue={{ value: '0', label: 'All' }}
                    options={accountManagers.map((option) => ({
                      value: option.id,
                      label: option.full_name
                    }))}
                  />
                </div>

                <ButtonsGroup
                  view={view}
                  viewState={viewState}
                  connectCallback={buttonsCallback}
                  userRole={userRole}
                  handleModal={handleModal}
                  loading={loading}
                  showTotal={showTotal}
                  onAddNewClientClick={onAddNewClientClick}
                />
              </>
            )}
          </div>
        </div>

        {children}

        {/* Table */}
        <div className="rounded-md border border-[#dee2e6] bg-white">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-gray-50">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width: header.column.columnDef.size,
                        textAlign:
                          header.column.columnDef.meta?.headerStyle
                            ?.textAlign || 'left'
                      }}
                      className="font-medium text-gray-500">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="hover:bg-gray-50/50"
                    onClick={() => rowEvents?.onClick?.(null, row.original)}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          textAlign:
                            cell.column.columnDef.meta?.cellStyle?.textAlign ||
                            'left',
                          paddingTop:
                            cell.column.columnDef.meta?.cellStyle?.paddingTop ||
                            '8px'
                        }}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tanstackColumns.length}
                    className="h-24 text-center">
                    {noDataIndication || 'No results.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Rows per page:
            </span>
            <Select
              styles={{
                ...customStyles,
                container: (provided) => ({ ...provided, width: '75px' })
              }}
              value={{
                value: pagination.pageSize,
                label: String(pagination.pageSize)
              }}
              onChange={(val) => {
                table.setPageSize(Number(val.value));
              }}
              options={[
                { value: 15, label: '15' },
                { value: 30, label: '30' },
                { value: 45, label: '45' },
                { value: 60, label: '60' }
              ]}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}>
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataTables;
